import dayjs from 'dayjs';
import _ from 'lodash';

import { EnumUtility, FormatUtility } from '@codequality/vue-cli-base';

import { E借入金条件, Eフェーズ種別, E金融借入科目 } from '@/openapi/enum';
import {
  CデータPlbscfItemCommon,
  E入力分類,
  E金融借入返済方法,
  PlbscfデータYearValue,
  YearMonthValue,
  データListItemResponse,
} from '@/openapi/user';

import { DAYJS_YM_NUMBER_FORMAT, DAYS_OF_YEAR, MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 決算期Utility, { 決算期 } from '@/classes/common/決算期Utility';
import 借入返済Utility from '@/classes/common/借入返済Utility';
import { PlbscfYearResult } from '@/classes/service/AbstractPlbscfService';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 金融機関借入Service, {
  金融機関借入,
  金融機関借入ListItem,
} from '@/classes/service/分析計画/金融機関借入Service';
import 役員借入Service, { 役員借入, 役員借入ListItem } from '@/classes/service/分析計画/役員借入Service';

// ------------------------------------------------------------------
// enums
// ------------------------------------------------------------------

/** 表種別 */
export enum TableType {
  短期 = '短期借入金返済予定表',
  長期 = '長期借入金返済予定表',
  役員 = '役員借入金返済予定表',
}

/** 行種別 */
export enum ListItemType {
  新規借入,
  元本返済,
  利息,
  残高,
}

/** セル種別 */
export enum CellTagType {
  inputCurrency,
  inputNumber,
  currency,
  textLeft,
  textRight,
}

// ------------------------------------------------------------------
// types
// ------------------------------------------------------------------

/** parse の返却値 */
export type 返済明細 = {
  v決算期: 決算期;
  v金融機関借入: 金融機関借入;
  v役員借入: 役員借入;
  tables: 返済明細Table[];
};

export type TOldLoan = {
  短期: number[];
  長期: number[];
  役員: number[];
  year: number;
};

export type TデータOldLoan = {
  データpk?: number;
  部門pk: number;
  items: TOldLoan;
};

/** 返済明細表 */
type 返済明細Table = {
  type: TableType;
  list: 返済明細ListItem[];
};

/** 返済明細表の行情報 */
export type 返済明細ListItem = {
  cells: (返済明細Cell | null)[];
  type: ListItemType;
  借入?: 金融機関借入ListItem | 役員借入ListItem; // 存在しない行は合計
};

/** 返済明細表のセル情報 */
export type 返済明細Cell = {
  tagType: CellTagType;
  fix: string | number; // 計算値/ラベル
  ymInput?: YearMonthValue; // Cデータ金融機関借入/役員借入の新規借入/返済元本/利息
  ym?: number; // 年月の列のみ設定
  colspan: number;
  rowspan: number;
};

/** 月次の計算情報 */
type Calc月次 = {
  新規借入: number;
  元本返済: number;
  利息: number;
  残高: number;
  input新規借入?: YearMonthValue;
  input元本返済?: YearMonthValue;
  input利息?: YearMonthValue;
};

/** 月次計算関数定義 */
type CalcFunction<T extends 金融機関借入ListItem | 役員借入ListItem> = (
  item: T,
  金利: number,
  前月残高: number,
  ym: number,
  findInput: findInputFunction,
  新規借入MonthlyValue: number | undefined,
  元本返済MonthlyValue: number | undefined,
  利息MonthlyValue: number | undefined
) => Calc月次;

type findInputFunction = (values: YearMonthValue[] | undefined) => YearMonthValue | undefined;

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

const COLUMNS_OF_YEAR = MONTHS_OF_YEAR + 1;

// ------------------------------------------------------------------
// 返済計画Service
// ------------------------------------------------------------------

export default class 返済明細Service extends AbstractデータService<返済明細> {
  // ------------------------------------------------------------------
  // fields
  // ------------------------------------------------------------------

  protected serviceType = ServiceType.返済明細;

  /** 表の見出し列数 */
  private readonly labelColCount = 3;

  /** 金融機関借入Service */
  private 金融機関借入Service: 金融機関借入Service | null = null;

  /** 役員借入Service */
  private 役員借入Service: 役員借入Service | null = null;

  /** 決算期 */
  private 決算期: 決算期;

  /** 月次計算結果保持用の返済明細表一覧 */
  private 月次返済明細Tables: 返済明細Table[] = [];

  /** 集計返済明細表一覧 */
  private 返済明細Tables: 返済明細Table[] = [];

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);

    this.決算期 = 決算期Utility.get計画決算期With0期月次(true);
  }

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * 行の列数getter
   *
   * @returns 行の列数
   */
  public get getColCountInRow(): number {
    if (this.決算期 === null) return 0;
    return this.labelColCount + this.決算期.header.length;
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(データs: データListItemResponse[], option?: Record<string, AbstractデータService<unknown>>): 返済明細 {
    // 各service初期化
    if (this.金融機関借入Service === null) {
      this.金融機関借入Service =
        option?.金融機関借入Service === undefined
          ? new 金融機関借入Service()
          : (option?.金融機関借入Service as 金融機関借入Service);
    }
    if (this.役員借入Service === null) {
      this.役員借入Service =
        option?.役員借入Service === undefined ? new 役員借入Service() : (option?.役員借入Service as 役員借入Service);
    }

    // 各serviceのparse
    const v金融機関借入 = this.金融機関借入Service.parse(データs);
    const v役員借入 = this.役員借入Service.parse(データs);

    const v返済明細: 返済明細 = {
      v決算期: this.決算期,
      v金融機関借入,
      v役員借入,
      tables: [],
    };

    // 返済明細Tablesの初期化
    this.返済明細Tables = this.initialize返済明細Tables(v返済明細);

    return v返済明細;
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const raw = this.parse(データs, option);

    // テンプレート行設定
    const templateRowConfig = {
      headerUnit: 0,
      headerYear: 1,
      tableTitle: 2,
      借入List: [3, 4, 5, 6],
      合計List: [7, 8, 9, 10],
    };

    // 行、列数の定数
    const loopRowCount = EnumUtility.keys(ListItemType).length;
    const valueColCount = raw.v決算期.header.length;

    // ヘッダー行設定
    const templateRows: number[] = [templateRowConfig.headerUnit, templateRowConfig.headerYear];
    const values: unknown[][] = [
      [AbstractデータService.getMoneyUnitLabel(), ...this.templateCellValues(this.getColCountInRow - 1)],
      [...this.templateCellValues(this.labelColCount), ...raw.v決算期.header.map((h) => h.label)],
    ];

    // データ行設定
    raw.tables.forEach((table) => {
      // 表タイトル
      templateRows.push(templateRowConfig.tableTitle);
      values.push([table.type, ...this.templateCellValues(this.getColCountInRow - 1)]);
      // データ
      table.list.forEach((item, i) => {
        const rowConfig = item.借入 !== undefined ? templateRowConfig.借入List : templateRowConfig.合計List;
        const rowValues = item.cells.map((cell) => {
          if (cell === null) return null;
          switch (cell.tagType) {
            case CellTagType.inputCurrency:
            case CellTagType.currency:
              const num = this.getCellValueNumber(cell);
              return this.getMoneyUnitValue(num);
            default:
              return this.getCellValue(cell);
          }
        });
        templateRows.push(rowConfig[i % loopRowCount]);
        values.push(rowValues);
      });
    });

    // 動的列設定
    const dynamicConfigs = [
      {
        columns: [0, 1, 2, ...new Array(valueColCount).fill(3)],
      },
    ];

    return {
      templateRows,
      values,
      dynamicConfigs,
    };
  }

  // ------------------------------------------------------------------
  // public methods
  // ------------------------------------------------------------------

  /**
   * 返済明細Listを初期化します
   *
   * @param v返済明細
   */
  public initialize返済明細Tables(v返済明細: 返済明細): 返済明細Table[] {
    // 決算期毎の年月一覧
    const 決算期Yms = this.決算期.years.map((y) => {
      const yms: number[] = [];
      for (let j = 0; j < MONTHS_OF_YEAR; j++) {
        const ym = dayjs()
          .year(y.year - 1)
          .month(this.決算期.fixMonth! + j)
          .startOf('month')
          .format(DAYJS_YM_NUMBER_FORMAT);
        yms.push(Number(ym));
      }
      return yms;
    });

    // 月次の返済明細表生成
    const tableTypes = [TableType.短期, TableType.長期, TableType.役員];
    this.月次返済明細Tables = tableTypes.map((type): 返済明細Table => {
      // TODO: 分岐をfilterだけにできるか検討
      let 借入返済明細List: 返済明細ListItem[];
      if (type === TableType.役員) {
        借入返済明細List = v返済明細.v役員借入.list.flatMap((item) =>
          this.createListItems(item, 決算期Yms, this.calculate月次役員借入)
        );
      } else {
        const v金融借入科目 = type === TableType.短期 ? E金融借入科目.短期借入金 : E金融借入科目.長期借入金;
        借入返済明細List = v返済明細.v金融機関借入.list
          .filter((item) => item.返済方法 !== E金融借入返済方法.その他)
          .filter((item) => item.科目 === v金融借入科目)
          .flatMap((item) => this.createListItems(item, 決算期Yms, this.calculate月次金融機関借入));
      }

      return {
        type: type,
        list: [...借入返済明細List, ...this.createTotalListItems(借入返済明細List)],
      };
    });

    // 月次の返済明細表から表示用の明細返済表を生成
    v返済明細.tables = this.月次返済明細Tables.map((table) => this.createTable(table, 決算期Yms));

    return v返済明細.tables;
  }

  /**
   * PLBSCF転記用の利息を集計したPlbscfデータYearValue一覧を取得します
   *
   * @returns PlbscfデータYearValue[]
   */
  public get利息YearValuesForPlbscf(tables: 返済明細Table[], 部門Pk: number, 科目Pk: number): PlbscfデータYearValue[] {
    /** 各返済明細表から利息合計行一覧を取得する関数 */
    const get利息Lists = (tables: 返済明細Table[]): 返済明細ListItem[] => {
      return tables.flatMap((table) =>
        _.cloneDeep(table.list.filter((item) => item.借入 === undefined && item.type === ListItemType.利息))
      );
    };

    // 各返済明細表から利息合計行を取得
    const 月次利息Lists = get利息Lists(this.月次返済明細Tables);
    const 決算期利息Lists = get利息Lists(tables);
    // 決算期Listは見出し列と0期月次を含むので除いておく
    決算期利息Lists.forEach((item) => {
      const removeCount = this.labelColCount + MONTHS_OF_YEAR;
      item.cells = item.cells.filter((_, i) => removeCount - 1 < i);
    });

    // 各合計行の合算とPlbscfデータYearValue一覧の生成
    const yearValues = this.決算期.years.map((y, i) => {
      // CデータPlbscfItemCommonの生成
      const values = 決算期利息Lists.flatMap((item) => this.getCellValueNumber(item.cells[i]));
      const common: CデータPlbscfItemCommon = {
        勘定科目Pk: 科目Pk,
        value: _.sum(values),
      };

      // 0,1期の場合はmonthlyValuesを集計
      if (y.is計画01) {
        const start = i * COLUMNS_OF_YEAR;
        common.monthlyValues = _.range(start, start + MONTHS_OF_YEAR).map((index) =>
          _.sum(月次利息Lists.map((item) => this.getCellValueNumber(item.cells[index])))
        );
      }

      return {
        year: y.year,
        values: [common],
        c部門pK: 部門Pk,
      };
    });

    return yearValues;
  }

  /**
   * 指定表種別の残高合計行をPlbscfYearResult一覧で取得します
   *
   * @param type
   * @returns PlbscfYearResult[]
   */
  public get残高合計PlbscfYearResults(type: TableType): PlbscfYearResult[] {
    /** 返済明細表一覧から指定TableTypeの返済明細表の残高合計行を取得する関数 */
    const get残高合計ListItem = (tables: 返済明細Table[], type: TableType): 返済明細ListItem | null => {
      const table = tables.find((table) => table.type === type);
      const list = table?.list.find((item) => item.借入 === undefined && item.type === ListItemType.残高);
      return list !== undefined ? _.cloneDeep(list) : null;
    };

    // 各返済明細表から残高合計行を取得
    const 決算期ListItem = get残高合計ListItem(this.返済明細Tables, type);
    const 月次ListItem = get残高合計ListItem(this.月次返済明細Tables, type);
    if (決算期ListItem === null || 月次ListItem === null) return []; // データが取得できない場合は空を返却
    // 決算期Listは見出し列と0期月次を含むので除いておく
    const removeCount = this.labelColCount + MONTHS_OF_YEAR;
    決算期ListItem.cells = 決算期ListItem.cells.filter((_, i) => removeCount - 1 < i);

    const results = this.決算期.years.map((y, i) => {
      const value = this.getCellValueNumber(決算期ListItem.cells[i]);
      const result: PlbscfYearResult = {
        year: y.year,
        calcValue: value ?? 0,
      };

      // 0,1期の場合はmonthlyValuesを集計
      if (y.is計画01) {
        const start = i * COLUMNS_OF_YEAR;
        result.monthlyCalcValues = _.range(start, start + MONTHS_OF_YEAR).map(
          (index) => this.getCellValueNumber(月次ListItem.cells[index]) ?? 0
        );
      }

      return result;
    });

    return results;
  }

  /**
   * 指定E借入条件と年月から月次の値を返却します
   * データが存在しない場合はnullを返却します
   *
   * @param e借入金条件
   * @param ym
   * @returns number | null
   */
  public findMonthlyValueBy借入金条件And年月(e借入金条件: E借入金条件, ym: number): number | null {
    let tableType = TableType.短期;
    let listItemType = ListItemType.新規借入;
    switch (e借入金条件) {
      case E借入金条件.入金短期:
        tableType = TableType.短期;
        listItemType = ListItemType.新規借入;
        break;
      case E借入金条件.入金長期:
        tableType = TableType.長期;
        listItemType = ListItemType.新規借入;
        break;
      case E借入金条件.入金役員:
        tableType = TableType.役員;
        listItemType = ListItemType.新規借入;
        break;
      case E借入金条件.返済短期:
        tableType = TableType.短期;
        listItemType = ListItemType.元本返済;
        break;
      case E借入金条件.返済長期:
        tableType = TableType.長期;
        listItemType = ListItemType.元本返済;
        break;
      case E借入金条件.返済役員:
        tableType = TableType.役員;
        listItemType = ListItemType.元本返済;
        break;
    }

    const table = this.月次返済明細Tables.find((table) => table.type === tableType);
    const list = table?.list.find((item) => item.借入 === undefined && item.type === listItemType);
    const cell = list?.cells.find((cell) => cell?.ym === ym);
    if (cell === undefined || cell === null) return null;
    return Number(cell.ymInput !== undefined ? cell.ymInput.value : cell.fix);
  }

  /**
   * tデータOldLoansを生成する
   *
   * @param データs
   * @param e科目別計画設定番号
   * @returns
   */
  public createTデータOldLoans(データs: データListItemResponse[]): TデータOldLoan {
    return this.入力分類Filter(データs, [E入力分類.OLDLOAN]).map((d) => ({
      データpk: d.pk,
      部門pk: d.c部門pK!,
      items: JSON.parse(d.cデータ),
    }))[0];
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 返済明細Cellに設定されている値を返却します
   * cellがundefined、nullの場合はnullを返却します
   *
   * @param cell
   * @returns string | number | null
   */
  private getCellValue(cell?: 返済明細Cell | null): string | number | null {
    if (cell === undefined || cell === null) return null;
    return cell.ymInput?.value ?? cell.fix;
  }

  /**
   * 返済明細Cellに設定されている値を数値で返却します
   * cellがundefined、null、もしくは値が数値以外の場合はnullを返却します
   *
   * @param cell
   * @returns number | null
   */
  private getCellValueNumber(cell?: 返済明細Cell | null): number | null {
    const num = Number(this.getCellValue(cell));
    return isNaN(num) ? null : num;
  }

  /**
   * 指定Ym（DAYJS_YM_NUMBER_FORMAT）のdayjsオブジェクトを取得します
   *
   * @param ym
   * @returns dayjs
   */
  private getDayjsFromYmNumber(ym: number): dayjs.Dayjs {
    return dayjs(String(ym), DAYJS_YM_NUMBER_FORMAT);
  }

  /**
   * 行のラベルを取得します
   *
   * @param type
   * @param isTotal
   * @returns string
   */
  private getRowLabel(type: ListItemType, isTotal = false): string {
    switch (type) {
      case ListItemType.新規借入:
        return isTotal ? '借入合計' : '新規借入';
      case ListItemType.元本返済:
        return isTotal ? '元本合計' : '元本返済';
      case ListItemType.利息:
        return isTotal ? '利息合計' : '利息';
      case ListItemType.残高:
        return isTotal ? '残高合計' : '残高';
    }
  }

  /**
   * 固定値のセル情報を生成します
   *
   * @param tagType
   * @param fix
   * @param colspan
   * @param rowspan
   * @returns 返済明細Cell
   */
  private createFixCell(
    tagType: CellTagType,
    fix: number | string,
    ym?: number,
    colspan = 1,
    rowspan = 1
  ): 返済明細Cell {
    return this.createCell(tagType, fix, undefined, ym, colspan, rowspan);
  }

  /**
   * セル情報を生成します
   *
   * @param tagType
   * @param fix
   * @param ymInput
   * @param ym
   * @param colspan
   * @param rowspan
   * @returns 返済明細Cell
   */
  private createCell(
    tagType: CellTagType,
    fix: number | string,
    ymInput?: YearMonthValue,
    ym?: number,
    colspan = 1,
    rowspan = 1
  ): 返済明細Cell {
    return {
      tagType,
      fix,
      ymInput,
      ym,
      colspan,
      rowspan,
    };
  }

  /**
   * 行情報を生成します
   *
   * @param item
   * @param 決算期Yms
   * @param calculator
   * @returns 返済明細ListItem[]
   */
  private createListItems<T extends 金融機関借入ListItem | 役員借入ListItem>(
    item: T,
    決算期Yms: number[][],
    calculator: CalcFunction<T>
  ): 返済明細ListItem[] {
    const 新規借入Cells: 返済明細Cell[] = [];
    const 元本返済Cells: 返済明細Cell[] = [];
    const 利息Cells: 返済明細Cell[] = [];
    const 残高Cells: 返済明細Cell[] = [];

    let 金利 = 0;
    if ('金融機関' in item) 金利 = item.当初金利 ?? 0;
    if ('役員名' in item) 金利 = item.金利 ?? 0;
    let 前月残高 = item.前期末残高 ?? 0;

    決算期Yms.forEach((yms, index) => {
      const is0期 = index === 0;
      const 期首残高 = 前月残高;

      const 決算期Ym = yms[yms.length - 1];
      const 決算期Year = 決算期Utility.getYear(決算期Ym);
      const find決算期Input: findInputFunction = (values) => {
        const found = values?.find((ymv) => ymv.ym === 決算期Year);
        if (found !== undefined) return found;

        // 第1期以降で旧形式 (YYYYMM) の入力データがあれば設定
        return is0期 ? undefined : values?.find((ymv) => ymv.ym === 決算期Ym);
      };

      const 新規借入input = find決算期Input(item.新規借入);
      const 元本返済input = find決算期Input(item.元本返済);
      const 利息input = find決算期Input(item.利息);

      const calcMonthlyValues = (ymv: YearMonthValue | undefined, is新規借入 = false): number[] | undefined => {
        if (is0期 || ymv === undefined) return undefined;
        return is新規借入
          ? [ymv.value, ...new Array(MONTHS_OF_YEAR - 1).fill(0)]
          : AbstractデータService.calculate月割りValues(ymv.value);
      };

      const 新規借入MonthlyValues = calcMonthlyValues(新規借入input, true);
      const 元本返済MonthlyValues = calcMonthlyValues(元本返済input);
      const 利息MonthlyValues = calcMonthlyValues(利息input);

      let 新規借入total = 0;
      let 元本返済total = 0;
      let 利息total = 0;

      // 月次の集計
      yms.forEach((ym, j) => {
        const findYmInput: findInputFunction = (values) => (is0期 ? values?.find((ymv) => ymv.ym === ym) : undefined);
        // 計算実行
        const calc月次 = calculator(
          item,
          金利,
          前月残高,
          ym,
          findYmInput,
          新規借入MonthlyValues?.[j],
          元本返済MonthlyValues?.[j],
          利息MonthlyValues?.[j]
        );

        // 各cellsに格納
        新規借入Cells.push(this.createCell(CellTagType.inputCurrency, calc月次.新規借入, calc月次.input新規借入, ym));
        元本返済Cells.push(this.createCell(CellTagType.inputCurrency, calc月次.元本返済, calc月次.input元本返済, ym));
        利息Cells.push(this.createCell(CellTagType.inputCurrency, calc月次.利息, calc月次.input利息, ym));
        残高Cells.push(this.createFixCell(CellTagType.currency, calc月次.残高, ym));

        新規借入total += calc月次.新規借入;
        元本返済total += calc月次.元本返済;
        利息total += calc月次.利息;
        前月残高 = calc月次.残高;
      });

      const tagType = is0期 ? CellTagType.currency : CellTagType.inputCurrency;
      const 期末残高 = 期首残高 + (新規借入input?.value ?? 新規借入total) - (元本返済input?.value ?? 元本返済total);

      新規借入Cells.push(this.createCell(tagType, 新規借入total, 新規借入input, 決算期Year));
      元本返済Cells.push(this.createCell(tagType, 元本返済total, 元本返済input, 決算期Year));
      利息Cells.push(this.createCell(tagType, 利息total, 利息input, 決算期Year));
      残高Cells.push(this.createFixCell(CellTagType.currency, 期末残高, 決算期Year));

      前月残高 = 期末残高;
    });

    const 新規借入List: 返済明細ListItem = {
      type: ListItemType.新規借入,
      cells: 新規借入Cells,
      借入: item,
    };
    const 元本返済List: 返済明細ListItem = {
      type: ListItemType.元本返済,
      cells: 元本返済Cells,
      借入: item,
    };
    const 利息List: 返済明細ListItem = {
      type: ListItemType.利息,
      cells: 利息Cells,
      借入: item,
    };
    const 残高List: 返済明細ListItem = {
      type: ListItemType.残高,
      cells: 残高Cells,
      借入: item,
    };

    return [新規借入List, 元本返済List, 利息List, 残高List];
  }

  /**
   * 金融機関借入の月次集計を行います
   *
   * @param item
   * @param 金利
   * @param 前月残高
   * @param ym
   * @param findYmInput
   * @param 新規借入MonthlyValue
   * @param 元本返済MonthlyValue
   * @param 利息MonthlyValue
   * @returns
   */
  private calculate月次金融機関借入: CalcFunction<金融機関借入ListItem> = (
    item,
    金利,
    前月残高,
    ym,
    findYmInput,
    新規借入MonthlyValue,
    元本返済MonthlyValue,
    利息MonthlyValue
  ) => {
    const result: Calc月次 = {
      新規借入: 0,
      元本返済: 0,
      利息: 0,
      残高: 0,
    };

    const ymDayjs = this.getDayjsFromYmNumber(ym);
    const 実行日Dayjs = item.実行日 !== undefined ? dayjs(item.実行日) : null;

    // 新規借入
    const input新規借入 = findYmInput(item.新規借入);
    let 新規借入 = 0;
    if (input新規借入 !== undefined) {
      新規借入 = input新規借入.value;
      result.input新規借入 = input新規借入;
    } else if (新規借入MonthlyValue !== undefined) {
      新規借入 = 新規借入MonthlyValue;
    } else if (実行日Dayjs !== null && item.借入金額 !== undefined) {
      if (ymDayjs.isSame(実行日Dayjs, 'month')) 新規借入 = item.借入金額;
    }

    // 元本返済
    const input元本返済 = findYmInput(item.元本返済);
    let 元本返済 = 0;
    let is元利均等 = false;
    if (input元本返済 !== undefined) {
      // inputがある場合は入力値を格納
      元本返済 = input元本返済.value;
      result.input元本返済 = input元本返済;
    } else if (元本返済MonthlyValue !== undefined) {
      元本返済 = 元本返済MonthlyValue;
    } else {
      // inputがない場合は返済元本を格納
      if (item.実行日 !== undefined && item.返済回数 !== undefined) {
        const 返済開始 = dayjs(item.元本返済開始日 ?? item.実行日).date(1);
        const 返済回数 = ymDayjs.diff(返済開始, 'month') + 1;

        switch (item.返済方法) {
          case E金融借入返済方法.元金均等:
            if (返済回数 === 1) {
              // 初回返済
              元本返済 = item.初回元本返済額 ?? 0;
            } else if (1 < 返済回数 && 返済回数 < item.返済回数) {
              // 2回目以降返済
              元本返済 = item.二回目以降元本返済額 ?? 0;
            } else if (返済回数 === item.返済回数) {
              // 最終回返済
              元本返済 = item.最終回元本返済額 ?? 0;
            }
            break;

          case E金融借入返済方法.元利均等:
            if (返済回数 < 1 || item.返済回数 < 返済回数) break;

            元本返済 = 借入返済Utility.calc元利均等(金利, item.返済回数, item.借入金額!);
            is元利均等 = true;
            break;

          case E金融借入返済方法.その他:
            break; // ここには来ないはず
        }
      }
    }

    // 利息
    const input利息 = findYmInput(item.利息);
    let 利息 = 0;
    if (input利息 !== undefined) {
      // inputがある場合は入力値を格納
      利息 = input利息.value;
      result.input利息 = input利息;
    } else if (利息MonthlyValue !== undefined) {
      利息 = 利息MonthlyValue;
    } else {
      // inputがない場合は利息計算結果（前月の残高*金利*前月の日数/年の日数）を格納
      const 前月日数 = ymDayjs.subtract(1, 'month').daysInMonth();
      利息 = 借入返済Utility.calc利息(前月残高, 金利, 前月日数, this.bnRoundingMode.実績計画);
    }

    if (is元利均等) 元本返済 -= 利息;

    // 残高
    const 残高 = 前月残高 + 新規借入 - 元本返済;

    // 月次に格納
    result.新規借入 = 新規借入;
    result.元本返済 = 元本返済;
    result.利息 = 利息;
    result.残高 = 残高;

    return result;
  };

  /**
   * 役員借入の月次集計を行います
   *
   * @param item
   * @param 金利
   * @param 前月残高
   * @param ym
   * @param findYmInput
   * @param 新規借入MonthlyValue
   * @param 元本返済MonthlyValue
   * @param 利息MonthlyValue
   * @returns
   */
  private calculate月次役員借入: CalcFunction<役員借入ListItem> = (
    item,
    金利,
    前月残高,
    ym,
    findYmInput,
    新規借入MonthlyValue,
    元本返済MonthlyValue,
    利息MonthlyValue
  ) => {
    const result: Calc月次 = {
      新規借入: 0,
      元本返済: 0,
      利息: 0,
      残高: 0,
    };

    const ymDayjs = this.getDayjsFromYmNumber(ym);
    const 返済開始Dayjs = item.返済開始時期 !== undefined ? dayjs(item.返済開始時期, 'YYYY/MM') : null;
    const is返済開始 = 返済開始Dayjs !== null && ymDayjs.isAfter(返済開始Dayjs, 'month');

    // 新規借入
    const input新規借入 = findYmInput(item.新規借入);
    let 新規借入 = 0;
    if (input新規借入 !== undefined) {
      新規借入 = input新規借入.value;
      result.input新規借入 = input新規借入;
    } else if (新規借入MonthlyValue !== undefined) {
      新規借入 = 新規借入MonthlyValue;
    }

    // 元本返済
    const input元本返済 = findYmInput(item.元本返済);
    let 元本返済 = 0;
    if (input元本返済 !== undefined) {
      // inputがある場合は入力値を格納
      元本返済 = input元本返済.value;
      result.input元本返済 = input元本返済;
    } else if (元本返済MonthlyValue !== undefined) {
      元本返済 = 元本返済MonthlyValue;
    } else {
      // inputがない場合は返済元本を格納
      if (is返済開始) {
        元本返済 = item.元本返済額月 ?? 0;
        if (前月残高 < 元本返済) 元本返済 = 前月残高;
      }
    }

    // 利息
    const input利息 = findYmInput(item.利息);
    let 利息 = 0;
    if (input利息 !== undefined) {
      // inputがある場合は入力値を格納
      利息 = input利息.value;
      result.input利息 = input利息;
    } else if (利息MonthlyValue !== undefined) {
      利息 = 利息MonthlyValue;
    } else {
      // inputがない場合は利息計算結果（残高*金利*月の日数/年の日数）を格納
      if (is返済開始) {
        const daysOfMonth = this.getDayjsFromYmNumber(ym).daysInMonth();
        利息 = this.calcPercentByBigNumber(
          前月残高,
          (金利 / 100) * daysOfMonth,
          DAYS_OF_YEAR,
          this.bnRoundingMode.実績計画
        );
      }
    }

    // 残高
    const 残高 = 前月残高 + 新規借入 - 元本返済;

    // 月次に格納
    result.新規借入 = 新規借入;
    result.元本返済 = 元本返済;
    result.利息 = 利息;
    result.残高 = 残高;

    return result;
  };

  /**
   * 合計の行情報を生成します
   *
   * @param 返済明細List
   * @returns 返済明細ListItem[]
   */
  private createTotalListItems(返済明細List: 返済明細ListItem[]): 返済明細ListItem[] {
    return (EnumUtility.keys(ListItemType) as (keyof typeof ListItemType)[]).map((key) => {
      const type = ListItemType[key];
      const list = 返済明細List.filter((item) => item.type === type);
      let totals: {
        ym: number;
        value: number;
      }[] = [];

      // 各行種別の集計
      list.forEach((item, i) => {
        // 最初の金融機関/役員の場合はtotalsを初期化
        if (i === 0) totals = item.cells.map((cell) => ({ ym: cell!.ym!, value: 0 }));
        // totalsの集計
        item.cells.forEach((cell, j) => {
          if (cell === null) return;
          const value = cell.ymInput !== undefined ? cell.ymInput.value : cell.fix;
          totals[j].value += Number(value);
        });
      });

      return {
        type,
        cells: totals.map((t) => this.createFixCell(CellTagType.currency, t.value, t.ym)),
      };
    });
  }

  /**
   * 月次返済明細Tableから表示用の返済明細Tableを生成します
   *
   * @param table
   * @param 決算期Yms
   */
  private createTable(table: 返済明細Table, 決算期Yms: number[][]): 返済明細Table {
    // 行情報生成
    const list = table.list.map((item): 返済明細ListItem => {
      const is合計 = item.借入 === undefined;
      const cells: (返済明細Cell | null)[] = [];

      // 見出し部分のセル設定
      if (is合計) {
        if (item.type === ListItemType.新規借入) {
          cells.push(this.createFixCell(CellTagType.textLeft, '合計', undefined, 2, 4));
          cells.push(null);
        } else {
          for (let i = 0; i < this.labelColCount - 1; i++) cells.push(null);
        }
        cells.push(this.createFixCell(CellTagType.textLeft, this.getRowLabel(item.type, true)));
      } else {
        const 借入 = item.借入!;
        const is金融機関 = '金融機関' in 借入;
        const is役員 = '役員名' in 借入;

        switch (item.type) {
          case ListItemType.新規借入:
            let 名称 = '';
            if (is金融機関) 名称 = 借入.金融機関 ?? '';
            if (is役員) 名称 = 借入.役員名 ?? '';
            cells.push(this.createFixCell(CellTagType.textRight, 借入.番号 ?? ''));
            cells.push(this.createFixCell(CellTagType.textLeft, 名称));
            cells.push(this.createFixCell(CellTagType.textLeft, this.getRowLabel(ListItemType.新規借入)));
            break;
          case ListItemType.元本返済:
            let 金利 = 0;
            if (is金融機関) 金利 = 借入.当初金利 ?? 0;
            if (is役員) 金利 = 借入.金利 ?? 0;
            cells.push(this.createFixCell(CellTagType.textLeft, '金利'));
            cells.push(this.createFixCell(CellTagType.textRight, `${FormatUtility.toFixed(金利, 2)}%`));
            cells.push(this.createFixCell(CellTagType.textLeft, this.getRowLabel(ListItemType.元本返済)));
            break;
          case ListItemType.利息:
            const 前期末残高 = 借入.前期末残高 ?? 0;
            cells.push(this.createFixCell(CellTagType.textLeft, '前期末残高'));
            cells.push(this.createFixCell(CellTagType.currency, 前期末残高));
            cells.push(this.createFixCell(CellTagType.textLeft, this.getRowLabel(ListItemType.利息)));
            break;
          case ListItemType.残高:
            let 借入期間 = '';
            if (is金融機関) 借入期間 = `${借入.実行日 ?? ''}-${借入.返済期日 ?? ''}`;
            cells.push(this.createFixCell(CellTagType.textLeft, '借入期間'));
            cells.push(this.createFixCell(CellTagType.textLeft, 借入期間));
            cells.push(this.createFixCell(CellTagType.textLeft, this.getRowLabel(ListItemType.残高)));
            break;
        }
      }

      // データ部分のセル設定
      決算期Yms[0].concat(this.決算期.years.map((item) => item.year)).forEach((ym) => {
        const cell = item.cells.find((cell) => cell?.ym === ym);
        if (cell !== undefined) cells.push(cell);
      });

      return {
        type: item.type,
        cells,
        借入: item.借入,
      };
    });

    return {
      type: table.type,
      list,
    };
  }
}
